import React, { useEffect, useState } from 'react';

import { startCase } from 'lodash';

import { Button, Modal } from '@shared/components/bootstrap';
import { Picture } from '@shared/components/helpers/picture';
import { ImgixBG, ImgixFit } from '@shared/utils/imgix';

import { Dialog as ErrorDialog } from '@admin/components/errors/dialog';
import { WarehouseSelectFormGroup } from '@admin/components/warehouses/select_form_group';
import { accountURL, locationURL, palletURL, transferURL } from '@admin/config/routes';

import { extractErrorMessage } from './api';
import { useCreateTransferRequest } from './hooks';
import { Transferable, TransferablesPallet, TransferRequestForm } from './types';

const gradeLabelClass = (score: string): string => {
  switch (score.toLowerCase()) {
    case 'good':
      return 'label-success';
    case 'acceptable':
      return 'label-warning';
    case 'fail':
      return 'label-danger';
    default:
      return 'label-default';
  }
};

const isPalletSelectable = (pallet: TransferablesPallet) =>
  !pallet.transfer_ids.length && !pallet.active_transfer_request;

const PalletCard: React.FC<{
  pallet: TransferablesPallet;
  selected: boolean;
  onToggle(checked: boolean): void;
}> = ({ pallet, selected, onToggle }) => (
  <div className="entry col-sm-3 col-xs-6">
    <div className="panel brick">
      {pallet.transfer_ids.length > 0 && (
        <div className="brick-alert">
          Requested on{' '}
          {pallet.transfer_ids.map((transferID, index) => (
            <React.Fragment key={transferID}>
              {index > 0 && ', '}
              <a className="btn-link" href={transferURL({ id: transferID })}>
                Transfer #{transferID}
              </a>
            </React.Fragment>
          ))}
        </div>
      )}
      {pallet.active_transfer_request && (
        <div className="brick-alert">Transfer Requested for {pallet.active_transfer_request.destination.name}</div>
      )}
      <div className="brick-label">
        {pallet.grade?.image?.source && (
          <figure className="brick-square">
            <Picture
              source={pallet.grade.image.source}
              width={320}
              height={320}
              fit={ImgixFit.Fill}
              background={ImgixBG.White}
              className="brick-image"
              alt=""
            />
          </figure>
        )}
        <div className="brick-metadata">
          <div>
            <a className="btn-link" href={palletURL(pallet.id)}>
              #{pallet.barcode.value}
            </a>{' '}
            <strong>({pallet.state})</strong>{' '}
            {pallet.grade?.score && (
              <span className={`label ${gradeLabelClass(pallet.grade.score)}`}>
                {startCase(pallet.grade.score.toLowerCase())}
              </span>
            )}
          </div>
          {pallet.location && (
            <div>
              Location:{' '}
              <a className="btn-link" href={locationURL(pallet.location.id)}>
                {pallet.location.barcode.value}
              </a>
            </div>
          )}
          <div>
            {pallet.customers && pallet.customers.length === 1 ? 'Customer:' : 'Customers:'}{' '}
            {pallet.customers?.map((customer, index) => (
              <span key={customer.id}>
                {index > 0 && ', '}
                <a className="btn-link" href={accountURL({ id: customer.account_id })}>
                  {customer.name}
                </a>
              </span>
            ))}
          </div>
          <div className="clearfix">
            <div className="pull-left">
              <input
                type="checkbox"
                aria-label={`Select pallet ${pallet.barcode.value}`}
                disabled={!isPalletSelectable(pallet)}
                checked={selected}
                onChange={(event) => onToggle(event.target.checked)}
              />
            </div>
            <div className="text-center">
              {pallet.requested_count !== pallet.items_count ? (
                <span>
                  <strong>{pallet.requested_count}</strong> of <span>{pallet.items_count}</span> items
                </span>
              ) : (
                <span>Full Pallet</span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

export const RequestTransferModal: React.FC<{
  transferable: Transferable;
  pallets: TransferablesPallet[];
  onClose(): void;
  onSuccess(): void;
}> = ({ transferable, pallets, onClose, onSuccess }) => {
  const [destinationID, setDestinationID] = useState<string | undefined>();
  const [error, setError] = useState<string | undefined>();
  const [createTransfer, { loading, response, error: requestError, reset }] = useCreateTransferRequest();

  useEffect(() => {
    if (!response) {
      return;
    }
    onSuccess();
    onClose();
    // Only react to the completed request response.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [response]);

  useEffect(() => {
    if (requestError) {
      setError(extractErrorMessage(requestError));
    }
  }, [requestError]);

  const save = () => {
    if (loading || !destinationID) {
      return;
    }

    const form: TransferRequestForm = {
      pallet_ids: pallets.map((pallet) => pallet.id),
      origin_id: transferable.warehouse.id,
      destination_id: Number(destinationID),
    };
    createTransfer(form);
  };

  return (
    <>
      {error && (
        <ErrorDialog
          title="Whoops"
          description={error}
          onClose={() => {
            setError(undefined);
            reset();
          }}
        />
      )}

      <Modal onClose={onClose}>
        <Modal.Content>
          <Modal.Body>
            <div className="text-center">
              <span>Transferring</span>{' '}
              <strong>
                {pallets.length} {pallets.length === 1 ? 'pallet' : 'pallets'}
              </strong>{' '}
              <span>from</span>{' '}
              <WarehouseSelectFormGroup
                id="transferables-origin-warehouse"
                className="form-control-inline"
                disabled
                value={String(transferable.warehouse.id)}
                onChange={() => undefined}
              />{' '}
              <span>to</span>{' '}
              <WarehouseSelectFormGroup
                id="transferables-destination-warehouse"
                className="form-control-inline"
                excludeIDs={[String(transferable.warehouse.id)]}
                value={destinationID}
                onChange={setDestinationID}
              />
            </div>

            <div className="dialog-actions">
              <Button kind="default" size="lg" onClick={onClose}>
                Cancel
              </Button>
              <Button kind="danger" size="lg" disabled={!!loading || !destinationID} onClick={save}>
                Request Pallet Transfer
              </Button>
            </div>
          </Modal.Body>
        </Modal.Content>
      </Modal>
    </>
  );
};

export const TransferablesPalletGrid: React.FC<{
  transferable: Transferable;
  selectedPalletIDs: Set<number>;
  onTogglePallet(pallet: TransferablesPallet, checked: boolean): void;
  onRequestTransfer(): void;
}> = ({ transferable, selectedPalletIDs, onTogglePallet, onRequestTransfer }) => {
  const schedulableCount = transferable.pallets.filter((pallet) => selectedPalletIDs.has(pallet.id)).length;

  return (
    <>
      <div className="panel">
        <div className="panel-body">
          <strong className="panel-descriptor">{transferable.warehouse.name}</strong>
          <div className="pull-right">
            <Button kind="primary" disabled={schedulableCount === 0} onClick={onRequestTransfer}>
              Request Selected Pallets
            </Button>
          </div>
        </div>
      </div>

      <div className="grid">
        <div className="row">
          {transferable.pallets.map((pallet) => (
            <PalletCard
              key={pallet.id}
              pallet={pallet}
              selected={selectedPalletIDs.has(pallet.id)}
              onToggle={(checked) => onTogglePallet(pallet, checked)}
            />
          ))}
        </div>
      </div>
    </>
  );
};
